import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { saveAmazonCaseData } from './caseReviewApi';

// Amazon case fields (LPN, removal order, case id, reimbursement) plus the
// email-template helper. Owns its own form state, seeded from the selected case.
export default function AmazonCaseForm({ selectedCase, emailTemplates, onSaved }) {
    const [lpn, setLpn] = useState('');
    const [removalOrderId, setRemovalOrderId] = useState('');
    const [amazonCaseId, setAmazonCaseId] = useState('');
    const [reimbursementId, setReimbursementId] = useState('');
    const [amazonReimbursementAmount, setAmazonReimbursementAmount] = useState('');
    const [saving, setSaving] = useState(false);

    const [selectedTemplate, setSelectedTemplate] = useState('');
    const [emailSubject, setEmailSubject] = useState('');
    const [emailBody, setEmailBody] = useState('');

    // Re-seed fields whenever a different case is selected.
    useEffect(() => {
        const d = selectedCase?.parsedData || {};
        setLpn(d.lpn || '');
        setRemovalOrderId(d.removal_order_id || '');
        setAmazonCaseId(d.amazon_case_id || '');
        setReimbursementId(d.reimbursement_id || '');
        setAmazonReimbursementAmount(d.amazon_reimbursement_amount || '');
        setSelectedTemplate('');
        setEmailSubject('');
        setEmailBody('');
    }, [selectedCase?.qr_case_id]); // eslint-disable-line react-hooks/exhaustive-deps

    const populateTemplate = (template) => {
        if (!template) return '';
        const d = selectedCase?.parsedData || {};
        const replacements = {
            '{{SELLER_SKU}}': d.seller_sku || 'N/A',
            '{{ASIN}}': d.asin1 || 'N/A',
            '{{QTY}}': d.qty || selectedCase?.quantity || '0',
            '{{FNSKU}}': d.fnsku || 'N/A',
            '{{REMOVAL_ORDER_ID}}': d.order_id || removalOrderId || 'N/A',
            '{{PRODUCT_NAME}}': d.item_name || 'N/A',
            '{{AMAZON_DISPOSITION}}': d.disposition || selectedCase?.disposition || 'N/A',
            '{{TRACKING}}': d.searchquery || 'N/A'
        };
        let out = template;
        Object.keys(replacements).forEach((ph) => {
            out = out.replace(new RegExp(ph, 'g'), replacements[ph]);
        });
        return out;
    };

    const handleTemplateChange = (templateKey) => {
        setSelectedTemplate(templateKey);
        const template = emailTemplates.find((t) => t.template_key === templateKey);
        setEmailSubject(template ? populateTemplate(template.subject_template) : '');
        setEmailBody(template ? populateTemplate(template.body_template) : '');
    };

    const copyText = (text, label) => {
        navigator.clipboard.writeText(text).then(
            () => toast.success(`Copied ${label}`),
            () => toast.error('Failed to copy')
        );
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await saveAmazonCaseData(selectedCase.qr_case_id, {
                lpn,
                removalOrderId,
                amazonCaseId,
                reimbursementId,
                amazonReimbursementAmount
            });
            toast.success('Amazon case data saved');
            if (onSaved) await onSaved();
        } catch (e) {
            toast.error('Failed to save Amazon case data');
        } finally {
            setSaving(false);
        }
    };

    const inputClass =
        'w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500';

    return (
        <div>
            <h3 className="font-semibold mb-3">Amazon Case Information</h3>
            <div className="space-y-3">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">LPN</label>
                    <input type="text" value={lpn} onChange={(e) => setLpn(e.target.value)} placeholder="Enter LPN" className={inputClass} />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Removal Order ID</label>
                    <input type="text" value={removalOrderId} onChange={(e) => setRemovalOrderId(e.target.value)} placeholder="Enter Removal Order ID" className={inputClass} />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Amazon Case ID</label>
                    <div className="flex gap-2">
                        <input type="text" value={amazonCaseId} onChange={(e) => setAmazonCaseId(e.target.value)} placeholder="Enter Amazon Case ID" className={`flex-1 ${inputClass}`} />
                        {amazonCaseId ? (
                            <a href={`https://sellercentral.amazon.com/cu/case-dashboard/view-case?caseID=${amazonCaseId}`} target="_blank" rel="noopener noreferrer" className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 whitespace-nowrap inline-flex items-center">View Case</a>
                        ) : (
                            <a href="https://sellercentral.amazon.com/help/center" target="_blank" rel="noopener noreferrer" className="bg-orange-600 text-white px-4 py-2 rounded-md hover:bg-orange-700 whitespace-nowrap inline-flex items-center">Create Case</a>
                        )}
                    </div>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Reimbursement ID</label>
                    <input type="text" value={reimbursementId} onChange={(e) => setReimbursementId(e.target.value)} placeholder="Enter Reimbursement ID" className={inputClass} />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Amazon Stated Reimbursement Amount</label>
                    <input type="text" value={amazonReimbursementAmount} onChange={(e) => setAmazonReimbursementAmount(e.target.value)} placeholder="Enter amount (e.g., $25.50)" className={inputClass} />
                </div>
                <button type="button" onClick={handleSave} disabled={saving} className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed">
                    {saving ? 'Saving...' : 'Save Amazon Case Data'}
                </button>

                <div className="mt-6 pt-6 border-t border-gray-200">
                    <h4 className="font-semibold mb-3">Email Template</h4>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Select Template</label>
                        <select value={selectedTemplate} onChange={(e) => handleTemplateChange(e.target.value)} className={inputClass}>
                            <option value="">Select a template...</option>
                            {emailTemplates.map((template) => (
                                <option key={template.template_key} value={template.template_key}>{template.template_name}</option>
                            ))}
                        </select>
                    </div>

                    {selectedTemplate && (
                        <>
                            <div className="mt-3">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                                <div className="flex gap-2">
                                    <input type="text" value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} className={`flex-1 ${inputClass}`} />
                                    <button type="button" onClick={() => copyText(emailSubject, 'subject')} className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 whitespace-nowrap">Copy Subject</button>
                                </div>
                            </div>
                            <div className="mt-3">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Body</label>
                                <textarea value={emailBody} onChange={(e) => setEmailBody(e.target.value)} rows={12} className={`${inputClass} font-mono text-sm`} />
                                <button type="button" onClick={() => copyText(emailBody, 'body')} className="mt-2 bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700">Copy Body</button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
